import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import './Login.css';
import packageJson from '../../package.json';

// Définir la limite d'utilisateurs maximale
const USER_LIMIT = 100;

function Login() {
  // États pour les champs du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  // États pour l'interface
  const [isSignUp, setIsSignUp] = useState(true); // Par défaut sur inscription (comme la landing)
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // États pour la gestion des utilisateurs
  const [limitReached, setLimitReached] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Initialisation du compteur d'utilisateurs (repris de votre version actuelle)
  useEffect(() => {
    const initUserCount = async () => {
      try {
        setInitializing(true);

        const statsRef = doc(db, 'Users', 'app_stats');
        const statsDoc = await getDoc(statsRef);

        if (statsDoc.exists()) {
          const data = statsDoc.data();
          const isLimitReached = (data.user_count || 0) >= USER_LIMIT;
          setLimitReached(isLimitReached);
        } else {
          const usersSnapshot = await getDocs(collection(db, 'Users'));
          let count = 0;

          usersSnapshot.forEach(doc => {
            if (doc.id !== 'app_stats') {
              count++;
            }
          });

          setLimitReached(count >= USER_LIMIT);

          await setDoc(statsRef, {
            user_count: count,
            limit_reached: count >= USER_LIMIT,
            last_updated: serverTimestamp(),
          });
        }
      } catch (error) {
        console.error("Erreur lors de l'initialisation du compteur:", error);
        setLimitReached(false);
      } finally {
        setInitializing(false);
      }
    };

    initUserCount();
  }, []);

  // Fonction pour traduire les messages d'erreur Firebase
  const translateFirebaseError = errorCode => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Adresse email invalide.';
      case 'auth/user-disabled':
        return 'Ce compte utilisateur a été désactivé.';
      case 'auth/user-not-found':
        return 'Aucun utilisateur trouvé avec cette adresse email.';
      case 'auth/wrong-password':
        return 'Mot de passe incorrect.';
      case 'auth/email-already-in-use':
        return 'Cette adresse email est déjà utilisée par un autre compte.';
      case 'auth/weak-password':
        return 'Le mot de passe est trop faible. Utilisez au moins 6 caractères.';
      case 'auth/network-request-failed':
        return 'Problème de connexion réseau. Vérifiez votre connexion internet.';
      case 'auth/popup-closed-by-user':
        return 'La fenêtre de connexion a été fermée avant la fin du processus.';
      case 'auth/cancelled-popup-request':
        return 'La demande de connexion a été annulée.';
      case 'auth/popup-blocked':
        return 'La fenêtre de connexion a été bloquée par votre navigateur.';
      default:
        return "Une erreur s'est produite. Veuillez réessayer plus tard.";
    }
  };

  // Mettre à jour le compteur après création d'utilisateur
  const incrementUserCount = async () => {
    try {
      const statsRef = doc(db, 'Users', 'app_stats');
      const statsDoc = await getDoc(statsRef);

      let newCount = 1;

      if (statsDoc.exists()) {
        const data = statsDoc.data();
        newCount = (data.user_count || 0) + 1;
      }

      await setDoc(statsRef, {
        user_count: newCount,
        limit_reached: newCount >= USER_LIMIT,
        last_updated: serverTimestamp(),
      });

      setLimitReached(newCount >= USER_LIMIT);

      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du compteur:', error);
      return false;
    }
  };

  // Gérer les changements dans les champs du formulaire
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Effacer les messages d'erreur quand l'utilisateur tape
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  // Fonction pour créer un compte
  const handleSignUp = async e => {
    e.preventDefault();

    // Validation côté client
    if (!formData.name.trim()) {
      setError('Veuillez entrer votre prénom.');
      return;
    }

    if (!formData.email.trim()) {
      setError('Veuillez entrer votre adresse email.');
      return;
    }

    if (!formData.password.trim()) {
      setError('Veuillez entrer un mot de passe.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    // Vérifier la limite d'utilisateurs
    if (limitReached) {
      setError(
        `Le nombre maximum d'utilisateurs (${USER_LIMIT}) est atteint. Les inscriptions sont temporairement suspendues.`
      );
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Créer l'utilisateur dans Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      // Envoyer un email de vérification
      await sendEmailVerification(userCredential.user);

      // Créer le document utilisateur dans Firestore
      const userRef = doc(db, 'Users', userCredential.user.uid);
      const userData = {
        name: formData.name.trim(),
        email: userCredential.user.email,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      };

      await setDoc(userRef, userData);

      // Incrémenter le compteur d'utilisateurs
      await incrementUserCount();

      // Réinitialiser le formulaire
      setFormData({ name: '', email: '', password: '' });

      setSuccessMessage(
        'Compte créé avec succès! Un email de vérification a été envoyé à votre adresse.'
      );
    } catch (error) {
      console.error("Erreur d'inscription:", error);
      setError(translateFirebaseError(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour se connecter
  const handleSignIn = async e => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setError('Veuillez entrer votre adresse email.');
      return;
    }

    if (!formData.password.trim()) {
      setError('Veuillez entrer votre mot de passe.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      // Mettre à jour la date de dernière connexion
      const userRef = doc(db, 'Users', userCredential.user.uid);
      await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });

      // Réinitialiser les champs
      setFormData({ name: '', email: '', password: '' });
    } catch (error) {
      console.error('Erreur de connexion:', error);
      setError(translateFirebaseError(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour se connecter avec Google
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Vérifier si l'utilisateur existe déjà
      const userRef = doc(db, 'Users', result.user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Si la limite est atteinte pour les nouveaux utilisateurs
        if (limitReached) {
          await auth.signOut();
          setError(
            `Le nombre maximum d'utilisateurs est atteint. Les inscriptions sont temporairement suspendues.`
          );
          setLoading(false);
          return;
        }

        // Créer le document utilisateur
        const userData = {
          name: result.user.displayName || 'Utilisateur Google',
          email: result.user.email,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
        };

        await setDoc(userRef, userData);
        await incrementUserCount();
      } else {
        // Mettre à jour la date de dernière connexion
        await setDoc(
          userRef,
          { lastLogin: serverTimestamp() },
          { merge: true }
        );
      }
    } catch (error) {
      console.error('Erreur de connexion Google:', error);
      setError(translateFirebaseError(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour réinitialiser le mot de passe
  const handleForgotPassword = async e => {
    e.preventDefault();

    if (!formData.email) {
      setError('Veuillez entrer votre adresse email.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await sendPasswordResetEmail(auth, formData.email);
      setSuccessMessage(
        'Un email de réinitialisation a été envoyé à votre adresse email.\n' +
          'Vérifiez aussi votre dossier spam si vous ne le voyez pas dans votre boîte de réception.'
      );
    } catch (error) {
      console.error('Erreur de réinitialisation du mot de passe:', error);
      setError(translateFirebaseError(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Changer entre inscription et connexion
  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccessMessage('');
    setFormData({ name: '', email: '', password: '' });
  };

  if (initializing) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="loading-spinner">
            <h2>Chargement...</h2>
            <p>Initialisation de l'application...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="main-layout">
        {/* COLONNE GAUCHE - CONTENU MARKETING */}
        <div className="marketing-content">
          {/* Header avec logo */}
          <header className="marketing-header">
            <div className="logo">
              <div className="logo-icon">🍽️</div>
              <span>Bobo Food Plan</span>
            </div>
          </header>

          {/* Hero Section */}
          <section className="hero-section">
            <h1 className="hero-title">
              Planifiez vos repas,
              <br />
              simplifiez vos courses
            </h1>
            <p className="hero-subtitle">
              L'application qui transforme votre organisation culinaire en
              quelques clics. Terminé le stress des "qu'est-ce qu'on mange ce
              soir ?"
            </p>
          </section>

          {/* Bénéfices */}
          <section className="benefits-section">
            <h2 className="section-title">Pourquoi choisir Bobo Food Plan ?</h2>

            <div className="benefit">
              <div className="benefit-icon">🗓️</div>
              <div className="benefit-content">
                <h3>Organisez vos repas de la semaine</h3>
                <p>
                  Créez votre planning hebdomadaire en glissant-déposant vos
                  recettes favorites. Visualisez votre semaine d'un coup d'œil
                  et ne restez plus jamais sans idées.
                </p>
              </div>
            </div>

            <div className="benefit">
              <div className="benefit-icon">🛒</div>
              <div className="benefit-content">
                <h3>Générez votre liste de courses</h3>
                <p>
                  Votre liste se crée automatiquement selon vos repas planifiés.
                  Fini les oublis, les achats inutiles et les allers-retours au
                  magasin.
                </p>
              </div>
            </div>

            <div className="benefit">
              <div className="benefit-icon">📚</div>
              <div className="benefit-content">
                <h3>Centralisez vos recettes</h3>
                <p>
                  Stockez et organisez toutes vos recettes avec catégories et
                  recherche. Retrouvez instantanément vos plats préférés et
                  découvrez de nouvelles saveurs.
                </p>
              </div>
            </div>
          </section>

          {/* Comment ça marche */}
          <section className="how-it-works">
            <h2 className="section-title">Comment ça marche ?</h2>

            <ol className="steps">
              <li className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Ajoutez vos recettes</h4>
                  <p>
                    Créez votre bibliothèque personnelle de recettes avec
                    ingrédients, instructions et catégories.
                  </p>
                </div>
              </li>

              <li className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Planifiez vos repas</h4>
                  <p>
                    Organisez votre semaine en glissant-déposant vos recettes
                    sur le calendrier.
                  </p>
                </div>
              </li>

              <li className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Faites vos courses</h4>
                  <p>
                    Utilisez la liste générée automatiquement et cochez vos
                    achats en magasin.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          {/* Footer */}
          <footer className="marketing-footer">
            <p>&copy; 2024 Bobo Food Plan - Version {packageJson.version}</p>
          </footer>
        </div>

        {/* COLONNE DROITE - FORMULAIRE D'AUTHENTIFICATION */}
        <div className="login-sidebar">
          <div className="login-container">
            {!showForgotPassword ? (
              <>
                <div className="login-header">
                  <h2 className="login-title">
                    {isSignUp
                      ? 'Rejoignez Bobo Food Plan'
                      : 'Bon retour parmi nous'}
                  </h2>
                  <p className="login-subtitle">
                    {isSignUp
                      ? 'Créez votre compte en 30 secondes'
                      : 'Connectez-vous à votre compte'}
                  </p>
                </div>

                {/* Affichage des erreurs et messages */}
                {error && <div className="alert alert-error">{error}</div>}
                {successMessage && (
                  <div className="alert alert-success">{successMessage}</div>
                )}

                {/* Message de limite atteinte */}
                {limitReached && isSignUp && (
                  <div className="alert alert-warning">
                    Le nombre maximum d'utilisateurs est atteint. Les
                    inscriptions sont temporairement suspendues.
                  </div>
                )}

                <form
                  className="login-form"
                  onSubmit={isSignUp ? handleSignUp : handleSignIn}
                >
                  {/* Champ prénom (uniquement pour l'inscription) */}
                  {isSignUp && (
                    <div className="form-group">
                      <label htmlFor="name">Prénom</label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={e =>
                          handleInputChange('name', e.target.value)
                        }
                        placeholder="Comment souhaitez-vous être appelé ?"
                        disabled={loading}
                      />
                    </div>
                  )}

                  {/* Champ email */}
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={e => handleInputChange('email', e.target.value)}
                      placeholder="votre@email.com"
                      disabled={loading}
                      required
                    />
                  </div>

                  {/* Champ mot de passe */}
                  <div className="form-group">
                    <label htmlFor="password">Mot de passe</label>
                    <input
                      type="password"
                      id="password"
                      value={formData.password}
                      onChange={e =>
                        handleInputChange('password', e.target.value)
                      }
                      placeholder={
                        isSignUp ? 'Minimum 6 caractères' : 'Votre mot de passe'
                      }
                      disabled={loading}
                      required
                      minLength="6"
                    />
                    {/* Lien mot de passe oublié (uniquement en mode connexion) */}
                    {!isSignUp && (
                      <button
                        type="button"
                        className="forgot-password-link"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Mot de passe oublié ?
                      </button>
                    )}
                  </div>

                  {/* Boutons d'authentification */}
                  <div className="auth-buttons">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading || (isSignUp && limitReached)}
                    >
                      {loading
                        ? isSignUp
                          ? 'Création...'
                          : 'Connexion...'
                        : isSignUp
                        ? 'Créer mon compte'
                        : 'Se connecter'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={toggleAuthMode}
                      disabled={loading}
                    >
                      {isSignUp
                        ? 'Déjà un compte ? Se connecter'
                        : 'Nouveau ? Créer un compte'}
                    </button>
                  </div>
                </form>

                {/* Divider */}
                <div className="divider">
                  <span>ou</span>
                </div>

                {/* Bouton Google */}
                <button
                  className="btn-google"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continuer avec Google
                </button>

                {/* Note de sécurité */}
                <p className="security-note">
                  ✨ Gratuit • Aucune carte bancaire requise • 100% sécurisé
                  <br />
                  <br />
                  En créant un compte, vous acceptez nos conditions
                  d'utilisation et notre politique de confidentialité.
                </p>
              </>
            ) : (
              /* FORMULAIRE RÉINITIALISATION MOT DE PASSE */
              <>
                <div className="login-header">
                  <h2 className="login-title">Mot de passe oublié ?</h2>
                  <p className="login-subtitle">
                    Entrez votre email pour recevoir un lien de réinitialisation
                  </p>
                </div>

                {error && <div className="alert alert-error">{error}</div>}
                {successMessage && (
                  <div className="alert alert-success">{successMessage}</div>
                )}

                <form className="login-form" onSubmit={handleForgotPassword}>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={e => handleInputChange('email', e.target.value)}
                      placeholder="votre@email.com"
                      disabled={loading}
                      required
                    />
                  </div>

                  <div className="auth-buttons">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Envoi...' : 'Envoyer le lien'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setError('');
                        setSuccessMessage('');
                      }}
                      disabled={loading}
                    >
                      Retour à la connexion
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
